/**
 * Step-by-Step Timeline JavaScript
 * Handles timeline progress animation and active states based on scroll
 * Based on main-changelog.js
 */

(() => {
  if (window.StepByStepTimeline) {
    if (window.initializeStepByStepTimelines) {
      window.initializeStepByStepTimelines();
    }
    return;
  }

class StepByStepTimeline {
  constructor(container) {
    this.container = container;
    this.stepsContainer = container.querySelector('[data-step-by-step-steps]');
    this.steps = container.querySelectorAll('.step-by-step__step');
    this.timelineBar = container.querySelector('.step-by-step__timeline-bar');
    this.timelineProgress = container.querySelector('.step-by-step__timeline-progress');
    
    // Mobile scroll state
    this.mobileScrollHandler = null;
    this.activeSteps = new Set();
    
    this.init();
  }

  init() {
    if (!this.stepsContainer) return;

    // Ensure setup happens in the right order
    this.setupInitialState();
    
    // Use requestAnimationFrame to ensure classes are applied before timeline calculation
    requestAnimationFrame(() => {
      this.initTimelineProgress();
      this.initMobileScrollEffect();
    });
  }

  setupInitialState() {
    this.addStepNumbers();
    this.markLastStep();
    this.addStepAnimations();
    
    // Force a reflow after applying classes
    if (this.steps.length > 0) {
      void this.steps[this.steps.length - 1].offsetHeight;
    }
  }

  addStepNumbers() {
    this.steps.forEach((step, index) => {
      const numberElement = step.querySelector('.step-by-step__step-number');
      if (numberElement) {
        numberElement.textContent = index + 1;
      }
    });
  }

  markLastStep() {
    // Remove 'is-last-step' class from all steps
    this.steps.forEach(step => {
      step.classList.remove('is-last-step');
    });
    
    // Add 'is-last-step' class to the actual last step
    if (this.steps.length > 0) {
      this.steps[this.steps.length - 1].classList.add('is-last-step');
    }
  }

  addStepAnimations() {
    this.steps.forEach((step, index) => {
      step.style.animationDelay = `${index * 0.1}s`;
    });
  }

  initTimelineProgress() {
    if (!this.timelineBar || !this.timelineProgress) return;

    // Store timeline state
    this.timelineInitialized = false;
    this.timelineScrollHandler = null;

    // Initialize or destroy timeline based on screen size
    const handleTimelineState = () => {
      const isDesktop = window.innerWidth >= 750;
      
      if (isDesktop && !this.timelineInitialized) {
        this.initializeTimeline();
      } else if (!isDesktop && this.timelineInitialized) {
        this.destroyTimeline();
      }
    };

    // Initial check
    handleTimelineState();

    // Listen for resize events
    window.addEventListener('resize', handleTimelineState);
  }

  initializeTimeline() {
    if (this.timelineInitialized) return;
    
    this.timelineInitialized = true;

    // Calculate timeline height to end at the last dot
    const calculateTimelineHeight = () => {
      if (this.steps.length === 0) return;
      
      const firstDot = this.steps[0].querySelector('.step-by-step__step-dot');
      const lastDot = this.steps[this.steps.length - 1].querySelector('.step-by-step__step-dot');
      
      if (!firstDot || !lastDot) return;
      
      // Force a reflow to ensure we get accurate measurements
      void firstDot.offsetHeight;
      void lastDot.offsetHeight;
      
      const firstDotTop = firstDot.getBoundingClientRect().top + window.scrollY;
      const lastDotTop = lastDot.getBoundingClientRect().top + window.scrollY;
      const containerTop = this.container.getBoundingClientRect().top + window.scrollY;
      
      // Calculate from center of first dot to center of last dot
      const firstDotCenter = firstDotTop + (firstDot.offsetHeight / 2);
      const lastDotCenter = lastDotTop + (lastDot.offsetHeight / 2);
      
      const timelineHeight = lastDotCenter - firstDotCenter;
      const timelineTop = firstDotCenter - containerTop;
      this.timelineBar.style.top = timelineTop + 'px';
      this.timelineBar.style.height = timelineHeight + 'px';
    };

    // Calculate timeline height on load and resize
    // Multiple strategies to ensure accurate calculation
    
    // Strategy 1: Immediate calculation with forced reflow
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        calculateTimelineHeight();
      });
    });
    
    // Strategy 2: Progressive recalculations
    setTimeout(calculateTimelineHeight, 50);
    setTimeout(calculateTimelineHeight, 150);
    setTimeout(calculateTimelineHeight, 350);
    setTimeout(calculateTimelineHeight, 600);
    
    // Strategy 3: Wait for fonts
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        setTimeout(calculateTimelineHeight, 100);
      });
    }
    
    // Strategy 4: Wait for window load (images and all resources)
    if (document.readyState === 'complete') {
      setTimeout(calculateTimelineHeight, 100);
    } else {
      window.addEventListener('load', () => {
        setTimeout(calculateTimelineHeight, 100);
      }, { once: true });
    }
    
    const resizeHandler = () => {
      calculateTimelineHeight();
    };
    
    window.addEventListener('resize', resizeHandler);
    
    // Use ResizeObserver to detect when steps change size
    if (window.ResizeObserver) {
      const resizeObserver = new ResizeObserver((entries) => {
        // Debounce to avoid too many recalculations
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          calculateTimelineHeight();
        }, 50);
      });
      
      // Observe all steps and the container
      this.steps.forEach(step => {
        resizeObserver.observe(step);
      });
      
      // Also observe the steps container
      if (this.stepsContainer) {
        resizeObserver.observe(this.stepsContainer);
      }
      
      // Store for cleanup
      this.resizeObserver = resizeObserver;
    }

    const updateProgress = () => {
      const scrollTop = window.scrollY + window.innerHeight / 2;
      const timelineTop = window.scrollY + this.timelineBar.getBoundingClientRect().top;
      const timelineHeight = this.timelineBar.clientHeight;

      let progressHeight;
      if (scrollTop < timelineTop) {
        progressHeight = 0;
      } else if (scrollTop > timelineHeight + timelineTop) {
        progressHeight = 100;
      } else {
        progressHeight = ((scrollTop - timelineTop) / timelineHeight) * 100;
      }
      
      this.timelineProgress.style.height = progressHeight + '%';

      // Update active dots based on center of dot
      this.steps.forEach(step => {
        const dot = step.querySelector('.step-by-step__step-dot');
        if (!dot) return;
        
        const dotTop = window.scrollY + dot.getBoundingClientRect().top;
        const dotCenter = dotTop + (dot.offsetHeight / 2);
        
        if (dotCenter < scrollTop) {
          step.classList.add('active');
        } else {
          step.classList.remove('active');
        }
      });
    };

    // Store scroll handler for cleanup
    this.timelineScrollHandler = updateProgress;

    // Initial update
    updateProgress();

    // Add scroll listener
    window.addEventListener('scroll', updateProgress);
  }

  destroyTimeline() {
    if (!this.timelineInitialized) return;
    
    this.timelineInitialized = false;

    // Remove scroll listener
    if (this.timelineScrollHandler) {
      window.removeEventListener('scroll', this.timelineScrollHandler);
      this.timelineScrollHandler = null;
    }

    // Disconnect ResizeObserver
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    
    // Clear any pending resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }

    // Reset timeline height
    if (this.timelineBar) {
      this.timelineBar.style.height = 'auto';
      this.timelineBar.style.top = '';
    }

    // Reset progress
    if (this.timelineProgress) {
      this.timelineProgress.style.height = '0%';
    }

    // Remove active classes from dots
    this.steps.forEach(step => {
      step.classList.remove('active');
    });
  }

  initMobileScrollEffect() {
    // Only initialize on mobile
    if (window.innerWidth >= 750) return;

    const updateMobileSteps = () => {
      const isMobile = window.innerWidth < 750;
      if (!isMobile) {
        this.destroyMobileScrollEffect();
        return;
      }

      this.steps.forEach(step => {
        const stepRect = step.getBoundingClientRect();
        const stepTop = stepRect.top;
        const stepBottom = stepRect.bottom;
        const viewportHeight = window.innerHeight;
        
        // Check if step is in viewport (with some offset)
        const isInViewport = stepTop < viewportHeight * 0.7 && stepBottom > viewportHeight * 0.3;
        
        if (isInViewport) {
          if (!this.activeSteps.has(step)) {
            step.classList.add('mobile-active');
            this.activeSteps.add(step);
          }
        } else {
          if (this.activeSteps.has(step)) {
            step.classList.remove('mobile-active');
            this.activeSteps.delete(step);
          }
        }
      });
    };

    // Store handler for cleanup
    this.mobileScrollHandler = updateMobileSteps;

    // Initial update
    updateMobileSteps();

    // Add scroll listener
    window.addEventListener('scroll', updateMobileSteps);
    
    // Add resize listener to handle orientation changes
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 750) {
        this.destroyMobileScrollEffect();
      } else if (!this.mobileScrollHandler) {
        this.initMobileScrollEffect();
      }
    });
  }

  destroyMobileScrollEffect() {
    if (this.mobileScrollHandler) {
      window.removeEventListener('scroll', this.mobileScrollHandler);
      this.mobileScrollHandler = null;
    }
    
    // Remove all mobile-active classes
    this.steps.forEach(step => {
      step.classList.remove('mobile-active');
    });
    
    this.activeSteps.clear();
  }

  // Cleanup method
  destroy() {
    this.destroyTimeline();
    this.destroyMobileScrollEffect();
  }

  // Public method for external access
  refresh() {
    this.steps = this.container.querySelectorAll('.step-by-step__step');
    this.addStepNumbers();
    this.markLastStep();
    this.addStepAnimations();
    this.initTimelineProgress();
  }
}

// Initialize all step-by-step timelines when DOM is ready
function initializeStepByStepTimelines() {
  const containers = document.querySelectorAll('[data-step-by-step]');
  containers.forEach(container => {
    if (container.dataset.stepByStepInitialized === 'true') return;

    container.dataset.stepByStepInitialized = 'true';
    container.stepByStepTimeline = new StepByStepTimeline(container);
  });
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  initializeStepByStepTimelines();
});

// Re-initialize when sections are loaded dynamically
document.addEventListener('shopify:section:load', () => {
  initializeStepByStepTimelines();
});

// Export for potential external use
window.StepByStepTimeline = StepByStepTimeline;
window.initializeStepByStepTimelines = initializeStepByStepTimelines;
})();
